import type { VerificationDetail } from '@repo/sdk/gen'
import { zVerificationGetNeighborsForResidenceData } from '@repo/sdk/zod'
import { Badge } from '@repo/ui/badge'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	notFound,
	stripSearchParams
} from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { useWorkingCall } from '../../../-hooks/use-working-call'
import { ApplicationDisabilityPriority } from '../../-components/application-disability-priority'
import {
	VERIFICATION_STATE_LABEL,
	VERIFICATION_STATE_TONE
} from '../-lib/labels'
import { verificationDetailQueryOptions } from '../-query/query-options'
import { VerificationArrows } from './-components/verification-arrows'
import { VerificationChecklist } from './-components/verification-checklist'
import { VerificationDocument } from './-components/verification-document'

const listFilters =
	zVerificationGetNeighborsForResidenceData.shape.body.shape.filters.unwrap()
		.shape

/** The filters of the list the card was opened from: the arrows follow them. */
const searchSchema = z.object({
	state: listFilters.state.catch(undefined),
	search: listFilters.search.catch(undefined)
})

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/verifiche/$verificationId/'
)({
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	// The loader reads a student's data and logs the access: only opening the
	// page runs it, never a hover on a link.
	preload: false,
	// The arrows move from card to card on this route: a new verification is a
	// new page, so the file shown beside the checklist is never another one's.
	remountDeps: ({ params }) => params.verificationId,
	loader: async ({ context, params }) => {
		try {
			await context.queryClient.ensureQueryData({
				...verificationDetailQueryOptions(
					params.residenceId,
					params.verificationId
				),
				revalidateIfStale: true
			})
		} catch (error) {
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				(error.code === 'verification_not_found' ||
					error.code === 'residence_not_found')
			) {
				throw notFound()
			}
			throw error
		}
	},
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<VerificationDetail>(
				queryClient,
				makeQueryKeys.verifications.detail(
					params.residenceId ?? '',
					params.verificationId ?? ''
				),
				(detail) =>
					i18n.t('admin-verifications:card.title', {
						protocol: detail.submission.protocol
					})
			)
	},
	component: VerificationCardPage
})

/**
 * One verification (FFD 2.13, steps 1 and 2): each item of the checklist with
 * what the student declared, the documents of the application beside them,
 * and the compliant outcome once every item is checked.
 */
function VerificationCardPage() {
	const { t } = useTranslation()
	const { residenceId, verificationId } = Route.useParams()
	const { readOnly } = useWorkingCall()
	const { data } = useSuspenseQuery(
		verificationDetailQueryOptions(residenceId, verificationId)
	)
	const { verification, files } = data
	// The file shown beside the checklist: the identity document first.
	const [fileId, setFileId] = useState(
		() =>
			(files.find((file) => file.item === 'identity') ?? files[0])?.id ??
			null
	)
	return (
		<main className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6'>
			<header className='flex flex-wrap items-start justify-between gap-3'>
				<div className='flex flex-col gap-2'>
					<div className='flex flex-wrap items-center gap-3'>
						<h1 className='font-semibold text-2xl'>
							{[verification.lastName, verification.firstName]
								.filter(Boolean)
								.join(' ') ||
								t('admin-verifications:card.not_given')}
						</h1>
						<Badge
							variant={
								VERIFICATION_STATE_TONE[verification.state]
							}
						>
							{t(VERIFICATION_STATE_LABEL[verification.state])}
						</Badge>
					</div>
					<p className='text-muted-foreground text-sm'>
						{t('admin-verifications:card.title', {
							protocol: data.submission.protocol
						})}{' '}
						· {t(`admin-verifications:quota.${verification.quota}`)}{' '}
						·{' '}
						{t(
							`admin-verifications:category.${verification.category}`
						)}
					</p>
				</div>
				<VerificationArrows />
			</header>
			<div className='grid min-w-0 gap-6 lg:grid-cols-2'>
				<div className='flex min-w-0 flex-col gap-6'>
					<VerificationChecklist
						detail={data}
						onShowFile={setFileId}
						residenceId={residenceId}
					/>
					{data.disabilityPriority && !readOnly && (
						<ApplicationDisabilityPriority
							residenceId={residenceId}
							state={data.disabilityPriority}
							submissionId={data.submission.id}
						/>
					)}
				</div>
				<VerificationDocument
					fileId={fileId}
					files={files}
					onFileChange={setFileId}
					residenceId={residenceId}
				/>
			</div>
		</main>
	)
}
